/**
 * Browser storage utilities
 */

/**
 * Test whether the given storage mechanism is available.
 *
 * Via https://developer.mozilla.org/en-US/docs/Web/API/Web_Storage_API/Using_the_Web_Storage_API#Feature-detecting_localStorage.
 *
 * @param {string} type 'localStorage', 'sessionStorage', etc.
 * @return {boolean}
 */
export function isStorageAvailable(type) {
  let storage;

  try {
    storage = window[type];
    const x = '__storage_test__';
    storage.setItem(x, x);
    storage.removeItem(x);
    return true;
  } catch (e) {
    return e instanceof DOMException
      && (
        // everything except Firefox
        22 === e.code
        // Firefox
        || 1014 === e.code
        // test name field too, because code might not be present
        // everything except Firefox
        || 'QuotaExceededError' === e.name
        // Firefox
        || 'NS_ERROR_DOM_QUOTA_REACHED' === e.name
      )
      // acknowledge QuotaExceededError only if there's something already stored
      && (storage && 0 !== storage.length);
  }
}

/**
 * Whether the client currently supports storage.
 *
 * @return {boolean}
 */
export function clientSupportsStorage() {
  // eslint-disable-next-line max-len
  return isStorageAvailable('localStorage') && isStorageAvailable('sessionStorage');
}

/**
 * Store a value in a client store.
 *
 * @param {string} key Storage key.
 * @param {any} value Value to store.
 * @param {string} storage Storage layer to use.
 */
export function store(key, value, storage) {
  if (! clientSupportsStorage()) {
    return;
  }

  try {
    window[storage].setItem(key, JSON.stringify(value));
  } catch (err) {
    // Ignore.
  }
}

/**
 * Retrieve a value from one of the client stores.
 *
 * @param {string} key Key to retrieve
 * @param {string} storage Storage layer to retrieve from.
 * @returns {any|null} Parsed value from storage or null if empty.
 */
export function retrieve(key, storage) {
  if (! clientSupportsStorage()) {
    return null;
  }

  let value = null;

  try {
    const raw = window[storage].getItem(key);
    value = JSON.parse(raw);
  } catch (err) {
    // Ignore.
  }

  return value;
}

